/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

// import tutorsParollTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData"

// import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";

// import CallbackRequestTableData from "Views/UsefulCorners/CallbackRequest/data/CallbackRequestTableData"

// import FaqsTableData from "Views/UsefulCorners/Faqs/data/FaqsTableData"

// import LevelLinkingTableData from "Views/UsefulCorners/LevelLinking/data/LevelLinkingTableData"

import ProductTableData from "Views/UsefulCorners/Product/data/ProductTableData";
import ProductEnqTableData from "Views/UsefulCorners/Product/data/ProductEnqTableData";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import api from "utils/api";

function Tables() {
  const [products, setProducts] = useState([]);
  const [productEnqs, setProductEnqs] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredProductEnqs, setFilteredProductEnqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enqLoading, setEnqLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [enqSearchQuery, setEnqSearchQuery] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEnqOpen, setDeleteEnqOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedEnqId, setSelectedEnqId] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchProductEnqs();
  }, []);

  useEffect(() => {
    // Filter products based on search query
    if (searchQuery.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.priceNormal?.toString().includes(searchQuery) ||
          product.discountPrice?.toString().includes(searchQuery)
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  useEffect(() => {
    // Filter product enquiries based on search query
    if (enqSearchQuery.trim() === "") {
      setFilteredProductEnqs(productEnqs);
    } else {
      const filtered = productEnqs.filter(
        (enq) =>
          enq.productName?.toLowerCase().includes(enqSearchQuery.toLowerCase()) ||
          enq.userName?.toLowerCase().includes(enqSearchQuery.toLowerCase()) ||
          enq.priceNormal?.toString().includes(enqSearchQuery) ||
          enq.discountPrice?.toString().includes(enqSearchQuery)
      );
      setFilteredProductEnqs(filtered);
    }
  }, [enqSearchQuery, productEnqs]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get("/products");
      if (response.data.success) {
        setProducts(response.data.data || []);
        setFilteredProducts(response.data.data || []);
      } else {
        toast.error("Failed to fetch products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error(error.response?.data?.message || "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductEnqs = async () => {
    try {
      setEnqLoading(true);
      const response = await api.get("/product-enqs");
      if (response.data.success) {
        setProductEnqs(response.data.data || []);
        setFilteredProductEnqs(response.data.data || []);
      } else {
        toast.error("Failed to fetch product enquiries");
      }
    } catch (error) {
      console.error("Error fetching product enquiries:", error);
      toast.error(error.response?.data?.message || "Failed to fetch product enquiries");
    } finally {
      setEnqLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedProductId(id);
    setDeleteOpen(true);
  };

  const handleDeleteEnqClick = (id) => {
    setSelectedEnqId(id);
    setDeleteEnqOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedProductId(null);
  };

  const handleDeleteEnqClose = () => {
    setDeleteEnqOpen(false);
    setSelectedEnqId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await api.delete(`/products/${selectedProductId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Product deleted successfully");
        fetchProducts(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(error.response?.data?.message || "Failed to delete product");
    } finally {
      handleDeleteClose();
    }
  };

  const handleDeleteEnqConfirm = async () => {
    try {
      const response = await api.delete(`/product-enqs/${selectedEnqId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Product enquiry deleted successfully");
        fetchProductEnqs(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete product enquiry");
      }
    } catch (error) {
      console.error("Error deleting product enquiry:", error);
      toast.error(error.response?.data?.message || "Failed to delete product enquiry");
    } finally {
      handleDeleteEnqClose();
    }
  };

  const { columns, rows } = ProductTableData(filteredProducts, handleDeleteClick, fetchProducts);
  const { columns: prCols, rows: prRows } = ProductEnqTableData(
    filteredProductEnqs,
    handleDeleteEnqClick,
    fetchProductEnqs
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={1}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Products {!loading && `(${filteredProducts.length})`}
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="search"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    sx={{ margin: "10px" }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <ArgonButton
                    size="small"
                    component={Link}
                    to="/product/addproduct"
                    variant="contained"
                    color="primary"
                  >
                    Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : filteredProducts.length === 0 ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <ArgonTypography variant="body2" color="text">
                    {searchQuery
                      ? "No products found matching your search"
                      : "No products available"}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>

        <Card>
          <ArgonBox p={2}>
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={1}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Product Enquiries {!enqLoading && `(${filteredProductEnqs.length})`}
              </ArgonTypography>
              <ArgonBox display="flex" alignItems="center">
                <ArgonInput
                  placeholder="search"
                  name="search"
                  size="small"
                  type="text"
                  variant="outlined"
                  sx={{ margin: "10px" }}
                  value={enqSearchQuery}
                  onChange={(e) => setEnqSearchQuery(e.target.value)}
                />
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>
          <ArgonBox
            sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} solid ${borderColor}`,
                },
              },
            }}
          >
            {enqLoading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress color="info" />
              </ArgonBox>
            ) : filteredProductEnqs.length === 0 ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                <ArgonTypography variant="body2" color="text">
                  {enqSearchQuery
                    ? "No product enquiries found matching your search"
                    : "No product enquiries available"}
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <Table columns={prCols} rows={prRows} />
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>

      <DeleteDialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        description="Are you sure you want to delete this product? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
      />

      <DeleteDialog
        open={deleteEnqOpen}
        onClose={handleDeleteEnqClose}
        onConfirm={handleDeleteEnqConfirm}
        title="Delete Product Enquiry"
        description="Are you sure you want to delete this product enquiry? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
